import { FormControl, FormGroup } from '@angular/forms';

export const VENDOR_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF'] as const;
export type VendorCurrency = typeof VENDOR_CURRENCIES[number];

export interface ExchangeRates {
  EUR: number;
  GBP: number;
  JPY: number;
  CHF: number;
}

export const DEFAULT_EXCHANGE_RATES: ExchangeRates = {
  EUR: 1.08,
  GBP: 1.27,
  JPY: 0.0067,
  CHF: 1.13,
};

export function normalizeExchangeRates(rates?: Partial<ExchangeRates>): ExchangeRates {
  return {
    EUR: positiveRate(rates?.EUR, DEFAULT_EXCHANGE_RATES.EUR),
    GBP: positiveRate(rates?.GBP, DEFAULT_EXCHANGE_RATES.GBP),
    JPY: positiveRate(rates?.JPY, DEFAULT_EXCHANGE_RATES.JPY),
    CHF: positiveRate(rates?.CHF, DEFAULT_EXCHANGE_RATES.CHF),
  };
}

export function rateForCurrency(currency: string, rates: ExchangeRates): number {
  if (!currency || currency === 'USD') {
    return 1;
  }
  return positiveRate(rates[currency], 0);
}

export function convertToUsd(amount: number, rate: number): number {
  const value = Number(amount);
  const exchange = Number(rate);
  if (!isFinite(value) || !isFinite(exchange) || exchange <= 0) {
    return 0;
  }
  return Math.round(value * exchange * 100) / 100;
}

/**
 * Writes the USD cost the retail formula uses, and keeps the original
 * amount, currency, rate, and conversion date on the same form.
 * Editing the USD amount marks it as an override until the original
 * amount or rate changes.
 */
export function syncVendorCost(
  form: FormGroup,
  currency: string,
  rates: ExchangeRates,
  source: 'original' | 'rate' | 'usd'
): void {
  const code = currency || 'USD';
  ensure(form, 'costOriginal', form.get('cost')?.value ?? 0);
  ensure(form, 'costCurrency', code);
  ensure(form, 'costExchangeRate', rateForCurrency(code, rates));
  ensure(form, 'costConvertedAt', '');
  ensure(form, 'costUsdOverridden', false);

  if (code === 'USD') {
    const usd = Number(form.get('cost')?.value) || 0;
    form.get('costOriginal').setValue(usd, { emitEvent: false });
    form.get('costCurrency').setValue('USD', { emitEvent: false });
    form.get('costExchangeRate').setValue(1, { emitEvent: false });
    form.get('costConvertedAt').setValue(new Date().toISOString(), { emitEvent: false });
    form.get('costUsdOverridden').setValue(false, { emitEvent: false });
    return;
  }

  const storedRate = Number(form.get('costExchangeRate').value);
  const currentRate = rateForCurrency(code, rates);
  // A newly entered vendor cost always uses the current rate. A manual USD
  // override keeps the rate already stored on that cost.
  const rate = source === 'original'
    ? (currentRate > 0 ? currentRate : storedRate)
    : (storedRate > 0 ? storedRate : currentRate);
  form.get('costCurrency').setValue(code, { emitEvent: false });
  form.get('costExchangeRate').setValue(rate, { emitEvent: false });

  const originalRaw = form.get('costOriginal').value;
  const original = Number(originalRaw);
  const existingUsd = Number(form.get('cost').value) || 0;
  const alreadyForeign = form.get('costUsdOverridden').value || (originalRaw !== '' && originalRaw != null && original > 0);
  // Frames saved before currency conversion only have a USD cost. Leave that
  // amount alone until someone types the vendor-currency cost or a rate.
  if (source !== 'usd' && !alreadyForeign && existingUsd > 0) {
    return;
  }

  form.get('costConvertedAt').setValue(new Date().toISOString(), { emitEvent: false });
  if (source === 'rate' && form.get('costUsdOverridden').value) {
    return;
  }
  if (source === 'usd') {
    form.get('costUsdOverridden').setValue(true, { emitEvent: false });
    return;
  }
  form.get('costUsdOverridden').setValue(false, { emitEvent: false });
  form.get('cost').setValue(convertToUsd(original, rate), { emitEvent: false });
}

function ensure(form: FormGroup, name: string, value: any): void {
  if (!form.get(name)) {
    form.addControl(name, new FormControl(value));
  }
}

function positiveRate(value: number, fallback: number): number {
  const parsed = Number(value);
  return isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
